import type { PropertyFilterQuery } from '@cloudscape-design/collection-hooks';
import type { GetMyItemsDashboardQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useFilterStore as useActivitiesFilterStore } from 'src/pages/assessments/activities/useFilterStore';

import {
  actionRegisterUrl,
  assessmentActivitiesRegisterPageUrl,
  assessmentRegisterUrl,
  controlRegisterUrl,
  indicatorRegisterUrl,
  issueRegisterUrl,
  obligationRegister,
  policyRegisterUrl,
  riskRegisterUrl,
} from '@/utils/urls';

import { useGetTypes } from './config';
import { useGetMyItemsFilteringTokens } from './hooks/useGetMyItemsFilteringTokens';

type PropertyFilterToken = {
  propertyKey: string;
  operator: string;
  value: unknown;
};

const isToken = (t: unknown): t is PropertyFilterToken =>
  typeof t === 'object' && t !== null && 'propertyKey' in t && 'operator' in t;

/**
 * Collects all non-Type tokens from the query's tokens and tokenGroups arrays.
 * These are the ribbon's status/other filter tokens that should be forwarded
 * to the destination register page.
 */
const extractNonTypeTokens = (query: PropertyFilterQuery): unknown[] => {
  const fromTokens = (query.tokens ?? []).filter(
    (t) => !isToken(t) || t.propertyKey !== 'Type'
  );
  const fromGroups = (query.tokenGroups ?? []).filter(
    (t) => !isToken(t) || t.propertyKey !== 'Type'
  );

  return [...fromTokens, ...fromGroups];
};

/**
 * Builds a filter query combining ribbon status tokens with ownership tokens.
 */
const buildFilterQuery = (
  nonTypeTokens: unknown[],
  ownershipTokens: unknown[]
) => ({
  tokenGroups: [
    ...nonTypeTokens,
    { tokens: ownershipTokens, operation: 'or' as const },
  ],
  tokens: [] as unknown[],
  operation: 'and' as const,
});

/**
 * Returns a click handler for CustomisableRibbon tiles on the My Items
 * dashboard. When click-through is enabled, inspects the tile's filter query
 * for a `Type = <entityType>` token and navigates to the corresponding
 * entity register page with status filters and ownership tokens applied.
 */
export const useRibbonNavigation = (
  clickThroughEnabled: boolean,
  data?: GetMyItemsDashboardQuery
): ((query: PropertyFilterQuery) => void) => {
  const navigate = useNavigate();
  const types = useGetTypes();
  const { getMyItemsFilteringTokens } = useGetMyItemsFilteringTokens();
  const { setRCSAActivityFilter } = useActivitiesFilterStore();

  return useCallback(
    (query: PropertyFilterQuery) => {
      if (!clickThroughEnabled) {
        return;
      }

      const allTokens = [...(query.tokens ?? []), ...(query.tokenGroups ?? [])];

      const typeToken = allTokens.find(
        (t): t is PropertyFilterToken =>
          isToken(t) && t.propertyKey === 'Type' && t.operator === '='
      );

      if (!typeToken) {
        return;
      }

      const typeValue = typeToken.value as string;
      const nonTypeTokens = extractNonTypeTokens(query);

      // Entity-to-data mapping for ownership token resolution
      const entityDataMap: Record<string, readonly unknown[] | undefined> = {
        [types.risk]: data?.risk,
        [types.action]: data?.action,
        [types.assessment]: data?.assessment,
        [types.control]: data?.control,
        [types.issue]: data?.issue,
        [types.document]: data?.document,
        [types.indicator]: data?.indicator,
        [types.obligationReview]: data?.obligation,
      };

      // RCSA uses store-based filtering with a custom owner property key
      if (typeValue === types.RCSAActivity) {
        const ownerTokens = getMyItemsFilteringTokens(
          data?.assessment_activity,
          'allAssignedUsers'
        );
        setRCSAActivityFilter(
          buildFilterQuery(nonTypeTokens, ownerTokens) as PropertyFilterQuery
        );
        navigate(assessmentActivitiesRegisterPageUrl());

        return;
      }

      const entityData = entityDataMap[typeValue];
      // getMyItemsFilteringTokens is typed per-entity but behaves the same for
      // every shape with owner/contributor fields; cast to satisfy the union.
      const ownerTokens = getMyItemsFilteringTokens(
        entityData as Parameters<typeof getMyItemsFilteringTokens>[0]
      );
      const filterQuery = buildFilterQuery(nonTypeTokens, ownerTokens);

      // Each entity's URL builder is typed against its own TypedPropertyFilterQuery<T>,
      // but they all accept the same structural shape. Cast to a common builder signature.
      type PatternAUrlBuilder = (filter: typeof filterQuery) => string;
      type PatternBUrlBuilder = (opts: {
        filtering: typeof filterQuery;
      }) => string;

      // Pattern B entities — filter wrapped in TableOptions { filtering: ... }
      const tableOptionsEntities = new Set([types.issue, types.indicator]);

      if (tableOptionsEntities.has(typeValue)) {
        const urlBuilderMap: Record<string, PatternBUrlBuilder | undefined> = {
          [types.issue]: issueRegisterUrl as unknown as PatternBUrlBuilder,
          [types.indicator]:
            indicatorRegisterUrl as unknown as PatternBUrlBuilder,
        };
        const urlBuilder = urlBuilderMap[typeValue];
        if (urlBuilder) {
          navigate(urlBuilder({ filtering: filterQuery }));
        }

        return;
      }

      // Pattern A entities — raw filter query passed directly
      const patternAMap: Record<string, PatternAUrlBuilder | undefined> = {
        [types.risk]: riskRegisterUrl as unknown as PatternAUrlBuilder,
        [types.action]: actionRegisterUrl as unknown as PatternAUrlBuilder,
        [types.assessment]:
          assessmentRegisterUrl as unknown as PatternAUrlBuilder,
        [types.control]: controlRegisterUrl as unknown as PatternAUrlBuilder,
        [types.document]: policyRegisterUrl as unknown as PatternAUrlBuilder,
        [types.obligationReview]:
          obligationRegister as unknown as PatternAUrlBuilder,
      };

      const urlBuilder = patternAMap[typeValue];
      if (urlBuilder) {
        navigate(urlBuilder(filterQuery));
      }
    },
    [
      clickThroughEnabled,
      navigate,
      data,
      types,
      getMyItemsFilteringTokens,
      setRCSAActivityFilter,
    ]
  );
};
